import { useEffect, useState, useCallback } from 'react';
import {
    AbstractRequest,
    cancelRequest as apiCancelRequest,
    approveRequest as apiApproveRequest,
    rejectRequest as apiRejectRequest,
} from "../api/RequestApi";
import {ApiError, ApiResponse} from "../api/types";


interface UseRequestsProps<T extends AbstractRequest, TCreateRequest = any, TValidationRequest = any, TValidationResult = any> {
    fetchRequests: () => Promise<ApiResponse<T[]>>;
    createRequest?: (requestData: TCreateRequest) => Promise<ApiResponse<T>>;
    validateRequest?: (requestData: TValidationRequest) => Promise<ApiResponse<TValidationResult>>;
    maxItems?: number;
}

interface UseRequestsReturn<T, TCreateRequest = any, TValidationRequest = any, TValidationResult = any> {
    requests: T[];
    loading: boolean;
    error: string | null;

    // Action states
    creating: boolean;
    validating: boolean;
    approving: boolean;
    rejecting: boolean;
    cancelling: boolean;

    // Actions (these handle list updates internally)
    create: (requestData: TCreateRequest) => Promise<T | null>;
    validate: (requestData: TValidationRequest) => Promise<TValidationResult | null>;
    approve: (request: T) => Promise<void>;
    reject: (request: T) => Promise<void>;
    cancel: (request: T) => Promise<void>;
    refreshRequests: () => Promise<void>;
}

export function useRequests<
    T extends AbstractRequest,
    TCreateRequest = any,
    TValidationRequest = any,
    TValidationResult = any
>({
      fetchRequests,
      createRequest,
      validateRequest,
      maxItems
  }: UseRequestsProps<T, TCreateRequest, TValidationRequest, TValidationResult>): UseRequestsReturn<T, TCreateRequest, TValidationRequest, TValidationResult> {

    const [requests, setRequests] = useState<T[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Individual action states
    const [creating, setCreating] = useState(false);
    const [validating, setValidating] = useState(false);
    const [approving, setApproving] = useState(false);
    const [rejecting, setRejecting] = useState(false);
    const [cancelling, setCancelling] = useState(false);

    useEffect(() => {
        loadRequests();
    }, []);

    const loadRequests = async () => {
        setLoading(true);
        setError(null);

        try {
            const response = await fetchRequests();
            setRequests(response.data || []);

        } catch (error) {
            console.error('Error loading requests:', error);
            setError('Failed to load requests');
            setRequests([]);

        } finally {
            setLoading(false);
        }
    };

    const refreshRequests = useCallback(async () => {
        await loadRequests();
    }, []);

    // Private: Add request to list
    const addRequest = useCallback((request: T) => {
        setRequests(prevRequests => {
            const newRequests = [request, ...prevRequests];
            return maxItems ? newRequests.slice(0, maxItems) : newRequests;
        });
    }, [maxItems]);

    // Private: Remove request from list
    const removeRequest = useCallback((request: T) => {
        setRequests(prevRequests =>
            prevRequests.filter(req => req.id !== request.id)
        );
    }, []);

    // Private: Update request in list
    const updateRequest = useCallback((requestId: string, updates: Partial<T>) => {
        setRequests(prevRequests =>
            prevRequests.map(req =>
                req.id === requestId ? { ...req, ...updates } : req
            )
        );
    }, []);

    // Public: Validate request (for form validation)
    const validate = useCallback(async (requestData: TValidationRequest): Promise<TValidationResult | null> => {
        if (!validateRequest) {
            console.warn('Validate function not provided to useRequests hook');
            return null;
        }

        setValidating(true);
        try {
            const response = await validateRequest(requestData);

            if (response.success && response.data) {
                return response.data;
            } else {
                throw new ApiError(response.error, 'Validation failed');
            }
        } catch (err) {
            console.error('Error validating request:', err);
            throw err;
        } finally {
            setValidating(false);
        }
    }, [validateRequest]);

    // Public: Create request (API call + add to list)
    const create = useCallback(async (requestData: TCreateRequest): Promise<T | null> => {
        if (!createRequest) {
            console.error('Create function not provided to useRequests hook');
            return null;
        }

        setCreating(true);
        try {
            const response = await createRequest(requestData);

            if (response.success && response.data) {
                addRequest(response.data);  // Internal update
                return response.data;
            } else {
                throw new ApiError(response.error, 'Failed to create request');
            }
        } catch (err) {
            console.error('Error creating request:', err);
            throw err;
        } finally {
            setCreating(false);
        }
    }, [createRequest, addRequest]);

    // Public: Approve request (API call + remove from list)
    const approve = useCallback(async (request: T) => {
        setApproving(true);
        try {
            const response = await apiApproveRequest<T>(request);

            if (response.success && response.data) {
                removeRequest(request);  // Internal update
                // Alternatively: updateRequest(request.id, { state: 'APPROVED' } as Partial<T>);
            } else {
                throw new ApiError(response.error, 'Failed to approve request');
            }
        } catch (err) {
            console.error('Error approving request:', err);
            throw err;
        } finally {
            setApproving(false);
        }
    }, [removeRequest]);

    // Public: Reject request (API call + remove from list)
    const reject = useCallback(async (request: T) => {
        setRejecting(true);
        try {
            const response = await apiRejectRequest<T>(request);

            if (response.success && response.data) {
                removeRequest(request);  // Internal update
                // Alternatively: updateRequest(request.id, { state: 'REJECTED' } as Partial<T>);
            } else {
                throw new ApiError(response.error, 'Failed to reject request');
            }
        } catch (err) {
            console.error('Error rejecting request:', err);
            throw err;
        } finally {
            setRejecting(false);
        }
    }, [removeRequest]);

    // Public: Cancel request (API call + remove from list)
    const cancel = useCallback(async (request: T) => {
        setCancelling(true);
        try {
            const response = await apiCancelRequest<T>(request);

            if (response.success && response.data) {
                removeRequest(request);  // Internal update
            } else {
                throw new ApiError(response.error, 'Failed to cancel request');
            }
        } catch (err) {
            console.error('Error cancelling request:', err);
            throw err;
        } finally {
            setCancelling(false);
        }
    }, [removeRequest]);

    return {
        requests,
        loading,
        error,
        creating,
        validating,
        approving,
        rejecting,
        cancelling,
        create,
        validate,
        approve,
        reject,
        cancel,
        refreshRequests
    };
}